// /g/CODE — private group invite landing page. Shows the group name and member
// count, then joins with the existing anonymous device identity.

import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Users, Loader2, MapPin, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { previewGroup, joinGroup } from "../lib/groups";
import { getProfile } from "../lib/driverProfile";

export default function GroupJoin() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [joining, setJoining] = React.useState(false);
  const [joined, setJoined] = React.useState(false);

  React.useEffect(() => {
    previewGroup(code)
      .then((d) => {
        setPreview(d);
        setJoined(!!d.already_member);
      })
      .catch((err) => setError(err?.response?.data?.detail || "This invite link is not valid."));
  }, [code]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      await joinGroup(code, getProfile()?.nickname);
      setJoined(true);
      toast.success("You're in the group.");
      setTimeout(() => navigate("/app"), 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join this group.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white font-body antialiased px-5 py-10 flex flex-col items-center" data-testid="group-join-root">
      <Link to="/" className="inline-flex items-center gap-2" data-testid="group-join-logo">
        <div className="w-7 h-7 rounded-md bg-[#ccff00] flex items-center justify-center">
          <MapPin className="w-4 h-4 text-[#0a0a0c]" strokeWidth={2.5} />
        </div>
        <span className="font-heading text-base font-semibold">
          SpotDrop<span className="text-[#ccff00]">.</span>
        </span>
      </Link>

      <div className="w-full max-w-md mt-10 p-5 rounded-3xl bg-[#141416] border border-white/10">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
          Private group invite
        </div>

        {error && (
          <>
            <div className="font-heading text-xl font-semibold mt-2" data-testid="group-join-error">
              {error}
            </div>
            <Link
              to="/app"
              className="mt-5 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2"
              data-testid="group-join-open-app"
            >
              Open SpotDrop <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}

        {!error && !preview && (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#ccff00]" />
          </div>
        )}

        {!error && preview && (
          <>
            <div className="font-heading text-2xl font-semibold mt-2 leading-tight" data-testid="group-join-name">
              {preview.name}
            </div>
            <div className="mt-1.5 text-[13px] text-zinc-400 inline-flex items-center gap-1.5" data-testid="group-join-count">
              <Users className="w-3.5 h-3.5 text-[#ccff00]" />
              {preview.member_count} member{preview.member_count === 1 ? "" : "s"}
            </div>

            <p className="mt-4 text-[13px] text-zinc-400 leading-relaxed">
              Share parking and location privately with people you trust. Location sharing is off
              until you switch it on, and you can stop it any time.
            </p>

            {joined ? (
              <button
                type="button"
                onClick={() => navigate("/app")}
                className="mt-5 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                data-testid="group-join-continue"
              >
                <Check className="w-4 h-4" />
                You&apos;re in — open SpotDrop
              </button>
            ) : (
              <button
                type="button"
                onClick={handleJoin}
                disabled={joining || preview.full}
                className="mt-5 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
                data-testid="group-join-btn"
              >
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                {preview.full ? "This group is full" : "Join group"}
              </button>
            )}

            <p className="mt-3 text-[10px] text-zinc-500 text-center leading-relaxed">
              No account needed. Nothing is shared until you choose to share it.
            </p>
          </>
        )}
      </div>

      <Toaster theme="dark" position="top-center" />
    </div>
  );
}
